/**
 * Offline IndexedDB storage for queuing submissions when offline.
 *
 * Database: `favoris-offline`
 * Store: `submissions` (auto-increment key)
 */

const DB_NAME = 'favoris-offline';
const DB_VERSION = 1;
const STORE_NAME = 'submissions';

function idbError(error: DOMException | null): Error {
  return error ?? new Error('IndexedDB request failed');
}

export interface OfflineSubmission {
  id?: number;
  type: 'url' | 'image';
  url?: string;
  blob?: ArrayBuffer;
  mimeType?: string;
  /** Dedup key: URL string for URLs, SHA-256 hex for images */
  dedupeKey: string;
  createdAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: 'id',
          autoIncrement: true,
        });
        store.createIndex('dedupeKey', 'dedupeKey', { unique: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(idbError(request.error));
  });
}

/**
 * Check whether a submission with the given dedupe key already exists.
 */
export async function hasSubmission(dedupeKey: string): Promise<boolean> {
  const db = await openDb();
  try {
    return await new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index('dedupeKey');
      const request = index.getKey(dedupeKey);
      request.onsuccess = () => resolve(request.result !== undefined);
      request.onerror = () => reject(idbError(request.error));
    });
  } finally {
    db.close();
  }
}

/**
 * Add a submission to the offline queue. Silently skips if a duplicate exists.
 * Returns true if actually inserted, false if deduplicated.
 */
export async function addSubmission(
  entry: Omit<OfflineSubmission, 'id'>
): Promise<boolean> {
  const db = await openDb();
  try {
    return await new Promise<boolean>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.add(entry);
      request.onsuccess = () => resolve(true);
      request.onerror = () => {
        // ConstraintError = duplicate dedupeKey (unique index)
        if (request.error?.name === 'ConstraintError') {
          tx.abort();
          resolve(false);
        } else {
          reject(idbError(request.error));
        }
      };
    });
  } finally {
    db.close();
  }
}

/**
 * Get all queued submissions, ordered by creation time.
 */
export async function getAllSubmissions(): Promise<OfflineSubmission[]> {
  const db = await openDb();
  try {
    return await new Promise<OfflineSubmission[]>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result as OfflineSubmission[]);
      request.onerror = () => reject(idbError(request.error));
    });
  } finally {
    db.close();
  }
}

/**
 * Remove a single submission by its auto-increment id.
 */
export async function removeSubmission(id: number): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(id);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(idbError(request.error));
    });
  } finally {
    db.close();
  }
}

/**
 * Count all queued submissions.
 */
export async function countSubmissions(): Promise<number> {
  const db = await openDb();
  try {
    return await new Promise<number>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.objectStore(STORE_NAME);
      const request = store.count();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(idbError(request.error));
    });
  } finally {
    db.close();
  }
}

/**
 * Remove all queued submissions.
 */
export async function clearAllSubmissions(): Promise<void> {
  const db = await openDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(idbError(request.error));
    });
  } finally {
    db.close();
  }
}

/**
 * Compute SHA-256 hex digest of an ArrayBuffer (for image dedup).
 */
export async function sha256Hex(data: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}
